import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { Package } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useLicenseRepository } from '../../utils/license-repository';
import { BulkMoveLocationState } from './bulk-move-location-state';
import { dedupePackagesBySlotId } from './bulk-move-transactions';
import { removeBulkTransactions } from './bulk-remove-transactions';
import { triggerOpenSlotForRemovalPackages, waitForOpenableRemovalPackages } from './wait-for-openable-drop-package';

export type BulkRemoveConfirmPhase = 'confirm' | 'opening' | 'done';

export interface BulkRemoveConfirmViewProps {
    phase: BulkRemoveConfirmPhase;
    transactions: ExtendedPudoTransaction[];
    openingPackages: Package[];
    isSubmitting: boolean;
    error: ReactNode | null;
    failureCount: number;
    successCount: number;
    canConfirm: boolean;
    handleConfirm: () => Promise<void>;
    handleOpeningContinue: () => void;
    handleBack: () => void;
}

export const useBulkRemoveConfirm = (): BulkRemoveConfirmViewProps => {
    const navigate = useKioskNavigate();
    const location = useLocation();
    const navigationState = useAppSelector((state) => state.navigation);
    const { getFunctionality, getFunctionalitySetting } = useLicenseRepository();

    const transactions = (location.state as BulkMoveLocationState | null)?.transactions ?? [];
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const actorUserId = navigationState.user?.id;
    const triggerLocationSpotId = navigationState.spot?.id;

    const bulkFunctionality = getFunctionality(Application.PUDO, Feature.BULK_MOVE_TRANSACTION);
    const bulkSettings = getFunctionalitySetting(bulkFunctionality?.id ?? '');
    const removePackageWorkflowId = bulkSettings?.[SettingKey.REMOVE_PACKAGE_WORKFLOW_ID] as string | undefined;

    const hasPickUpPending = useMemo(() => transactions.some((tx) => tx.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING), [transactions]);

    const [phase, setPhase] = useState<BulkRemoveConfirmPhase>('confirm');
    const [openingPackages, setOpeningPackages] = useState<Package[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<ReactNode | null>(null);
    const [failureCount, setFailureCount] = useState(0);
    const [successCount, setSuccessCount] = useState(0);

    const canConfirm = !!accountId && transactions.length > 0 && !isSubmitting && phase === 'confirm' && (!hasPickUpPending || !!removePackageWorkflowId);

    const startRemovalOpening = useCallback(
        async (packages: Package[]) => {
            if (!accountId || packages.length === 0) return;

            const openable = await waitForOpenableRemovalPackages(accountId, packages);
            await triggerOpenSlotForRemovalPackages(accountId, openable, {
                location: triggerLocationSpotId,
                actor: actorUserId
            });

            const uiPackages = dedupePackagesBySlotId(openable.length > 0 ? openable : packages);
            flushSync(() => {
                setOpeningPackages(uiPackages);
                setPhase('opening');
            });
        },
        [accountId, actorUserId, triggerLocationSpotId]
    );

    const handleConfirm = useCallback(async () => {
        if (!accountId || transactions.length === 0 || isSubmitting) return;
        if (hasPickUpPending && !removePackageWorkflowId) {
            setError(CommonMessage.ERRORS.DEFAULT);
            return;
        }

        setIsSubmitting(true);
        setError(null);

        // Mount door UI before PACKAGE_SKIP so workflow MQTT open events are not missed.
        if (hasPickUpPending) {
            const previewPackages = transactions
                .filter((tx) => tx.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING)
                .map((tx) => ({ slot_id: tx.slot_id ?? tx.slot?.entity_id }) as Package)
                .filter((pkg) => !!pkg.slot_id);

            if (previewPackages.length > 0) {
                flushSync(() => {
                    setOpeningPackages(previewPackages);
                    setPhase('opening');
                });
            }
        }

        try {
            const result = await removeBulkTransactions(
                transactions.map((tx) => ({
                    accountId,
                    transactionId: tx.id,
                    transactionState: tx.state as TransactionStateName,
                    removePackageWorkflowId,
                    actorUserId,
                    triggerLocationSpotId,
                    currentSpotId: tx.spot_id,
                    currentSlotId: tx.slot_id ?? tx.slot?.entity_id
                }))
            );

            setSuccessCount(result.successes.length);
            setFailureCount(result.failures.length);

            if (result.failures.length > 0 && result.successes.length === 0) {
                Logger.error('Bulk remove failed for all transactions', result.failures);
                setError(CommonMessage.ERRORS.DEFAULT);
                setPhase('confirm');
                setOpeningPackages([]);
                return;
            }

            if (result.removalPackages.length > 0) {
                await startRemovalOpening(result.removalPackages);
                return;
            }

            if (result.openingPackages.length > 0) {
                setOpeningPackages(result.openingPackages);
                setPhase('opening');
                return;
            }

            setPhase('done');
        } catch (err) {
            Logger.error('Bulk remove failed', err);
            setError(CommonMessage.ERRORS.DEFAULT);
            setPhase('confirm');
            setOpeningPackages([]);
        } finally {
            setIsSubmitting(false);
        }
    }, [accountId, actorUserId, hasPickUpPending, isSubmitting, removePackageWorkflowId, startRemovalOpening, transactions, triggerLocationSpotId]);

    const handleOpeningContinue = useCallback(() => {
        navigate('/spots/layout/pudo/bulk-move-transactions');
    }, [navigate]);

    const handleBack = useCallback(() => {
        if (phase === 'opening' || phase === 'done') return;
        navigate(-1);
    }, [navigate, phase]);

    return {
        phase,
        transactions,
        openingPackages,
        isSubmitting,
        error: error ?? (hasPickUpPending && !removePackageWorkflowId && phase === 'confirm' ? CommonMessage.ERRORS.DEFAULT : null),
        failureCount,
        successCount,
        canConfirm,
        handleConfirm,
        handleOpeningContinue,
        handleBack
    };
};
